// Turning a failed request into something to show the user.
//
// Kept separate from useApi so the mapping can be tested on its own: it is the
// only place that decides whether a failure is worth a toast and whether it
// should move the user somewhere else.
//
// It returns catalogue keys, not text: the wording depends on the language the
// user has when the toast shows, which is decided by the caller (useApi), at
// that moment. Only a failure with no known code or status carries its own raw
// `message` (the server's status text or the transport error).

// Codes the API returns in `{"error": "..."}`. A code the backend chose is
// always more useful than a guess made from the status alone.
const API_ERRORS = {
	"not-subscribed": {
		messageKey: "errors:api.not-subscribed",
		level: "warning",
		redirectTo: "/plans",
	},
	// Legacy pricing cannot be returned to once left, so the plan is fixed for
	// these subscribers by design. Saying "maybe you are on the wrong account"
	// and throwing them to the home page told them nothing and lost their place.
	"legacy-plan-locked": {
		messageKey: "errors:api.legacy-plan-locked",
		level: "warning",
		redirectTo: null,
	},
	"legacy-plan-unavailable": {
		messageKey: "errors:api.legacy-plan-unavailable",
		level: "error",
		redirectTo: null,
	},
	"invalid-payment-plan": {
		messageKey: "errors:api.invalid-payment-plan",
		level: "error",
		redirectTo: null,
	},
	"subscriptions-not-configured": {
		messageKey: "errors:api.subscriptions-not-configured",
		level: "error",
		redirectTo: null,
	},
};

const BY_STATUS = {
	401: {
		messageKey: "errors:status.401",
		level: "error",
		redirectTo: "/login",
	},
	403: {
		messageKey: "errors:status.403",
		level: "warning",
		redirectTo: "/",
	},
	// Silent on purpose: forms render their own field errors, and a toast on
	// top of them is noise. A 400 carrying a known code is handled above.
	400: { messageKey: "errors:status.400", level: null, redirectTo: null },
	404: {
		messageKey: "errors:status.404",
		level: "error",
		redirectTo: null,
	},
	422: {
		messageKey: "errors:status.422",
		level: "error",
		redirectTo: null,
	},
	500: {
		messageKey: "errors:status.500",
		level: "error",
		redirectTo: null,
	},
};

export function resolveRequestError(error) {
	const response = error?.response;

	const code = response?.data?.error;
	if (code && API_ERRORS[code]) return { message: null, ...API_ERRORS[code] };

	const byStatus = BY_STATUS[response?.status];
	if (byStatus) return { message: null, ...byStatus };

	// Nothing we have words for: show what the server or the network said, and
	// only if it said nothing at all, the generic line.
	const raw = response?.statusText || error?.message || null;
	return {
		messageKey: raw ? null : "errors:unknown",
		message: raw,
		level: "error",
		redirectTo: null,
	};
}
